import { getConfig } from '@/configs/config-accessor';
import { handleGetCollection } from '@/constants/db-collection.constants';
import ApiService from './ApiService';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

export interface IOrderUpdateType {
  action: 'OrderStatusUpdate' | 'flaggedUpdate' | 'importantUpdate';
  payload: IOrderType;
}

export interface PaymentSummary {
  subTotal: number;
  tax: number;
  deliveryFees: number;
  discountAmount: string;
  total: number;
}

export interface Shipping {
  deliveryFees: number;
  estimatedMin: number;
  estimatedMax: number;
  shippingLogo: string;
  shippingVendor: string;
}

export interface ShippingAddress {
  deliveryAddress: string;
  location: string;
  lat: string;
  long: string;
  cityName: string;
}

export interface Customer {
  name: string;
  email: string;
  phone: string;
  img: string;
  previousOrder: number;
  shippingAddress: ShippingAddress;
}

export interface Event {
  time: number;
  action: string;
  recipient: string;
}

export interface Activity {
  date: Date;
  events: Event[];
}

export interface Option {
  optionID: number;
  optionName: string;
  optionGroupName: string;
  price: number;
  quantity: number;
}

export interface Product {
  itemID: number;
  productName: string;
  itemImage: string;
  quantity: number;
  categoryName: string;
  minimumDelivery: number;
  sizeID: number;
  price: number;
  totalProductPrice: number;
  discountGiven: number;
  options: Option[];
}

export interface IOrderType {
  _id: string;
  id: string;
  platform: string;
  customerAddress: string;
  phone: string;
  remarks: string;
  fullName: string;
  orderType: string;
  voucher: string;
  createdAt: Date;
  payementStatus: string;
  progressStatus: string;
  orderStatus: string;
  paymentType: string;
  appVersion: number;
  isFlagged: boolean;
  isImportant: boolean;
  paymentSummary: PaymentSummary;
  shipping: Shipping;
  customer: Customer;
  activity: Activity[];
  products: Product[];
}

export type CreateOrder_Body = Omit<Partial<IOrderType>, '_id'>;
export type CreateOrder_Response = IResponse<IOrderType | null>;
export const createOrder = async (body: Partial<CreateOrder_Body>) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<CreateOrder_Response, DynamicCreate_Body<CreateOrder_Body>>({ 
    url: `${newApiPrefix}/dynamic/create`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('order'), 
      data: body 
    } 
  });
};

export type GetOrderById_Params = { _id: string };
export type GetOrderById_Response = IResponse<IOrderType | null>;
export const getOrderById = async (params: GetOrderById_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetOrderById_Response>({ 
    url: `${newApiPrefix}/dynamic/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('order'), 
      filter: { id: params._id } 
    } 
  });
};

export type GetOrderByFilter = { filter: Record<string, unknown> };
export type GetOrders_Response = IResponse<Array<IOrderType> | null>;
export const getOrdersList = async (params: DynamicGet_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetOrders_Response, DynamicGet_Body>({ 
    url: `${newApiPrefix}/dynamic/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('order'), 
      filter: params.filter, 
      select: params.select, 
      sort: { createdAt: 'desc' } 
    } 
  });
};

export type UpdateOrder_Params = { _id: string };
export type UpdateOrder_Body = { data: Partial<IOrderType | unknown> };
export type UpdateOrder_Response = IResponse<IOrderType | null>;
export const updateOrderApi = async (params: UpdateOrder_Params, body: UpdateOrder_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<UpdateOrder_Response, DynamicUpdate_Body<UpdateOrder_Body>>({ 
    url: `${newApiPrefix}/dynamic/update`, 
    method: 'patch', 
    data: { 
      collection: handleGetCollection('order'), 
      filter: { id: params._id }, 
      data: body.data 
    } 
  });
};

export type DeleteOrder_Params = { _id: string };
export type DeleteOrder_Response = IResponse<IOrderType | null>;
export const deleteOrder = async (params: DeleteOrder_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<DeleteOrder_Response, DynamicDelete_Body>({ 
    url: `${newApiPrefix}/dynamicmongodb/delete`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('order'), 
      filter: { id: params._id } 
    } 
  });
};
